import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Clock,
  CreditCard,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  LogOut,
  RefreshCw,
  Loader2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function PendingApproval({ user, logout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    checkApprovalAndFetchStatus();
  }, []);

  const checkApprovalAndFetchStatus = async () => {
    try {
      setLoading(true);
      
      // First check if user is already approved
      const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (meRes.data && !meRes.data.is_pending) {
        // User is already approved, redirect to dashboard
        navigate("/dashboard");
        return;
      }
      
      // Fetch pending payments
      const paymentsRes = await axios.get(`${API}/payments/my-pending`, { withCredentials: true });
      setPendingPayments(paymentsRes.data || []);
      
      // Fetch registration status
      try {
        const statusRes = await axios.get(`${API}/academy/my-registration-status`, { withCredentials: true });
        setRegistrationStatus(statusRes.data);
      } catch (e) {
        console.log("Could not fetch registration status");
      }
      
    } catch (error) {
      console.error("Error fetching status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      
      // Fetch pending payments
      const paymentsRes = await axios.get(`${API}/payments/my-pending`, { withCredentials: true });
      setPendingPayments(paymentsRes.data || []);
      
      // Fetch registration status
      try {
        const statusRes = await axios.get(`${API}/academy/my-registration-status`, { withCredentials: true });
        setRegistrationStatus(statusRes.data);
      } catch (e) {
        console.log("Could not fetch registration status");
      }
      
    } catch (error) {
      console.error("Error fetching status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStatus();
    
    // Check if user is now approved
    try {
      const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (meRes.data && !meRes.data.is_pending) {
        toast.success("Your account has been approved!");
        navigate("/dashboard");
        return;
      }
    } catch (error) {
      console.error("Error checking approval status:", error);
    }
    
    setRefreshing(false);
    toast.info("Status refreshed - still pending approval");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading your status...</p>
        </div>
      </div>
    );
  }

  const hasPaymentToComplete = pendingPayments.some(p => p.action_required === "confirm_payment");
  const hasPaymentPendingVerification = pendingPayments.some(p => p.action_required === "wait_for_verification");

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-black">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">⚽</span>
            <span className="font-bold text-xl text-yellow-500">SCC Football</span>
          </div>
          <Button variant="ghost" onClick={logout} className="text-slate-400 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Status Card */}
        <Card className="bg-slate-800/50 border-amber-500/30 mb-6">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 mx-auto rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
              <Clock className="w-10 h-10 text-amber-400" />
            </div>
            <CardTitle className="text-2xl text-amber-400">Account Pending Approval</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-slate-300 mb-4">
              Welcome, <strong className="text-white">{user?.given_name || user?.email}</strong>!
            </p>
            <p className="text-slate-400 text-sm mb-6">
              Your registration has been submitted. Please complete any pending actions below.
            </p>
            
            <Button 
              onClick={handleRefresh} 
              variant="outline" 
              className="border-amber-500/50 text-amber-400"
              disabled={refreshing}
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Check Status
            </Button>
          </CardContent>
        </Card>

        {/* Action Cards */}
        <div className="space-y-4">
          {/* Complete Payment Card */}
          {hasPaymentToComplete && (
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-6 h-6 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-red-400 mb-2">⚠️ Payment Required</h3>
                    <p className="text-slate-300 text-sm mb-4">
                      You have a pending payment that needs to be completed to finalize your registration.
                    </p>
                    
                    {pendingPayments.filter(p => p.action_required === "confirm_payment").map((payment, idx) => (
                      <div key={idx} className="bg-slate-900/50 p-4 rounded-lg mb-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-slate-400">Reference:</span>
                          <span className="font-mono text-white">{payment.payment_reference}</span>
                        </div>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-slate-400">Amount:</span>
                          <span className="font-bold text-xl text-white">${payment.amount?.toFixed(2)} SGD</span>
                        </div>
                        {payment.hours_remaining && (
                          <p className="text-xs text-amber-400 mb-3">
                            ⏳ Expires in {Math.floor(payment.hours_remaining)}h {Math.round((payment.hours_remaining % 1) * 60)}m
                          </p>
                        )}
                        <Link to={`/academy/paynow-payment?ref=${payment.payment_reference}&amount=${payment.amount}&txn=${payment.id}`}>
                          <Button className="w-full bg-red-600 hover:bg-red-700">
                            Complete Payment
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Pending Verification Card */}
          {hasPaymentPendingVerification && !hasPaymentToComplete && (
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-emerald-400 mb-2">✓ Payment Submitted</h3>
                    <p className="text-slate-300 text-sm mb-4">
                      Your payment is being verified by our admin team. This usually takes 1-2 business days.
                    </p>
                    
                    {pendingPayments.filter(p => p.action_required === "wait_for_verification").map((payment, idx) => (
                      <div key={idx} className="bg-slate-900/50 p-3 rounded-lg text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Reference:</span>
                          <span className="font-mono text-white">{payment.payment_reference}</span>
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-slate-400">Amount:</span>
                          <span className="text-white">${payment.amount?.toFixed(2)} SGD</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Pending Payments */}
          {pendingPayments.length === 0 && (
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-blue-400 mb-2">Awaiting Admin Approval</h3>
                    <p className="text-slate-300 text-sm">
                      Your registration is being reviewed. You&apos;ll be able to access the full dashboard once approved.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Help Section */}
        <Card className="mt-6 bg-slate-800/30 border-slate-700">
          <CardContent className="p-4">
            <p className="text-sm text-slate-400 text-center">
              Need help? Contact us at{" "}
              <a href="mailto:academy@sccfootball.com" className="text-yellow-400 hover:underline">
                academy@sccfootball.com
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
